import { useState, CSSProperties } from 'react';
import { Popover } from 'antd';
import { TableOutlined } from '@ant-design/icons';
import { ToolbarTooltip } from '../ViewerControls/ToolbarTooltip';
import styles from './MultiViewerOptionButton.module.css';
import type { GridLayout } from '../ViewerContext';

const popoverStyles = {
  body: {
    background: 'rgba(30, 31, 34, 0.95)',
    backdropFilter: 'blur(8px)',
    padding: '8px',
  },
} as const;

const popoverOverlayStyle: CSSProperties = {
  // Remove default ant design arrow/border styling
};

const GRID_SIZE = 4;

interface MultiViewerOptionButtonProps {
  selectedLayout: GridLayout;
  onLayoutChange: (layout: GridLayout) => void;
}

function MultiViewerOptionButton({
  selectedLayout,
  onLayoutChange,
}: MultiViewerOptionButtonProps) {
  const [open, setOpen] = useState(false);
  const [hoverCell, setHoverCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  // Convert 1-indexed layout to 0-indexed cell position for display
  const selectedCell = {
    row: selectedLayout.rows - 1,
    col: selectedLayout.cols - 1,
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setHoverCell(null);
    }
  };

  const handleCellClick = (row: number, col: number) => {
    // Convert 0-indexed cell position to 1-indexed layout
    onLayoutChange({ rows: row + 1, cols: col + 1 });
    setOpen(false);
  };

  const isHighlighted = (row: number, col: number): boolean => {
    const activeCell = hoverCell ?? selectedCell;
    return row <= activeCell.row && col <= activeCell.col;
  };

  const gridContent = (
    <div className={styles.layoutGrid} onMouseLeave={() => setHoverCell(null)}>
      {Array.from({ length: GRID_SIZE }).map((__, row) =>
        Array.from({ length: GRID_SIZE }).map((___, col) => (
          <div
            // eslint-disable-next-line react/no-array-index-key -- Fixed grid positions
            key={`grid-${row}-${col}`}
            role="button"
            tabIndex={0}
            aria-label={`Select ${row + 1}×${col + 1} layout`}
            className={styles.layoutCell}
            data-highlighted={isHighlighted(row, col)}
            onMouseEnter={() => setHoverCell({ row, col })}
            onClick={() => handleCellClick(row, col)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleCellClick(row, col);
            }}
          />
        ))
      )}
    </div>
  );

  return (
    <Popover
      placement="bottom"
      content={gridContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      overlayStyle={popoverOverlayStyle}
      styles={popoverStyles}
      arrow={false}
      overlayInnerStyle={{
        background: 'rgba(30, 31, 34, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '6px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      }}
    >
      <ToolbarTooltip
        title="Change grid layout"
        open={open ? false : undefined}
      >
        <button
          type="button"
          className={styles.toolButton}
          data-active={open}
          aria-label="Change grid layout"
        >
          <TableOutlined />
        </button>
      </ToolbarTooltip>
    </Popover>
  );
}

export default MultiViewerOptionButton;
